import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import * as amplitude from "@amplitude/analytics-browser";

import PropTypes from "prop-types";

import { selectIsSkuInCart, selectCartSkuQuantity } from "../../redux/cart";
import { selectMechanicId } from "../../redux/auth";

import { useCart } from "../../hooks/useCart";

import { logSkuOutOfStockQuantity } from "../../services/sku";

import { APP_PATHS } from "../../constants/paths";

import QuantityControls from "../QuantityControls/QuantityControls";
import AddedToCart from "../UI/AddedToCart/AddedToCart";
import LoadingOverlay from "../UI/Loading/LoadingOverlay/LoadingOverlay";
import ImagePlaceholder from "../UI/ImagePlaceholder/ImagePlaceholder";
import Price from "../Price/Price";
import { AddToCartBtn } from "../sku";
import { OutOfStockModal } from "../UI/Modals";

import classNames from "classnames";
import classes from "./SkuCard.module.css";

const SkuCard = (props) => {
  const {
    className,
    id,
    imageUrl,
    brandName,
    name,
    price,
    availableQuantity,
    isIncludedInBundles,
    isCartRuleApplied,
  } = props;

  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);

  const userId = useSelector(selectMechanicId);
  const isAddedToCart = useSelector((state) => selectIsSkuInCart(state, id));
  const cartSkuQuantity = useSelector((state) =>
    selectCartSkuQuantity(state, id)
  );
  const {
    skuQuantityIncreasedHandler,
    skuQuantityDecreasedHandler,
    isLoading,
  } = useCart();

  function closeOutOfStockModalHandler() {
    setIsOutOfStockModalOpen(false);
  }

  const increaseQuantityHandler = async () => {
    if (cartSkuQuantity >= availableQuantity) {
      try {
        logSkuOutOfStockQuantity(id, cartSkuQuantity + 1);
        amplitude.track("order item quantity out of stock", {
          userId,
          skuId: id,
          quantity: cartSkuQuantity + 1,
        });
      } catch (err) {
        console.error(err);
      }
      setIsOutOfStockModalOpen(true);
      return;
    }
    await skuQuantityIncreasedHandler(id, price, cartSkuQuantity, 1);
  };

  const decreaseQuantityHandler = async () => {
    await skuQuantityDecreasedHandler(id, price, cartSkuQuantity, 1);
  };

  return (
    <div className={classNames(classes["sku-card"], className)}>
      <div className={classes["sku-card__details-container"]}>
        <div className={classes["sku-card__image-container"]}>
          <ImagePlaceholder
            src={imageUrl}
            alt={name}
            loading="lazy"
            className={classes["sku-card__image"]}
          />
        </div>
        <div className={classes["sku-card__details"]}>
          <p className={classes["sku-card__brand-name"]}>{brandName}</p>
          <Link
            to={APP_PATHS.SKU_DETAILS.url.replace(":skuId", id)}
            className={classes["sku-card__name"]}
          >
            {name}
          </Link>
        </div>
      </div>
      <div className={classes["sku-card__footer"]}>
        <div className={classes["sku-card__price-wrapper"]}>
          <Price
            value={price}
            suffix="جنيه"
            className={classes["sku-card__price"]}
            valueClassName={classes["sku-card__price-value"]}
            suffixClassName={classes["sku-card__price-currency"]}
          />
          {isIncludedInBundles && (
            <Link
              className={classes["sku-card__bundle-offer"]}
              to={`${APP_PATHS.BUNDLES.url}?skuId=${id}`}
            >
              شوف العروض المتوفرة
            </Link>
          )}
        </div>

        {!isAddedToCart ? (
          <AddToCartBtn
            className={classes["sku-card__add-to-cart-btn"]}
            id={id}
            price={price}
            imageUrl={imageUrl}
            name={name}
            availableQty={availableQuantity}
            qtyToAdd={1}
          />
        ) : (
          <div className={classes["sku-card__quantity-wrapper"]}>
            <QuantityControls
              className={classes["sku-card__quantity-controls"]}
              quantity={cartSkuQuantity}
              onPlus={increaseQuantityHandler}
              onMinus={decreaseQuantityHandler}
            />
            <AddedToCart
              isSkuAdded={true}
              className={classes["sku-card__added-to-cart"]}
            />
          </div>
        )}
      </div>
      {isLoading && <LoadingOverlay />}
      {isOutOfStockModalOpen && (
        <OutOfStockModal
          isOpen={isOutOfStockModalOpen}
          onClose={closeOutOfStockModalHandler}
          skuId={id}
          imageUrl={imageUrl}
          name={name}
          price={price}
          message="بنعتذر عن عدم توفر الكمية"
          showNotifyMe={!isCartRuleApplied}
        />
      )}
    </div>
  );
};

export default SkuCard;

SkuCard.propTypes = {
  id: PropTypes.number,
  imageUrl: PropTypes.string,
  brandName: PropTypes.string,
  name: PropTypes.string,
  price: PropTypes.number,
  availableQuantity: PropTypes.number,
  isIncludedInBundles: PropTypes.bool,
  isCartRuleApplied: PropTypes.bool,
};
